import { useParams, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import PageContainer from "@/components/layout/page-container";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { useGetOpportunity } from "@/services/opportunities/queries";
import { useAuthStore } from "@/store/auth";

export default function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, context } = useAuthStore();
  const activeOrganisationId = user?.organisation?.id;
  const activeWorkspaceId = user?.workspace?.workspaceId;

  // Call the getOpportunity API
  const { data: opportunityData, isLoading } = useGetOpportunity(id || "", {
    enabled: !!id,
  });

  // Check if the opportunity belongs to the user's organisation
  const opportunityOrgId = opportunityData?.ownerEntity?.id || null;
  const ownerEntityId = activeOrganisationId || activeWorkspaceId;
  const isOwnOpportunity = ownerEntityId && opportunityOrgId === ownerEntityId;

  // Check if user is a workspace admin (workspace admins cannot apply)
  const isWorkspaceAdmin =
    context === "workspace" && user?.workspace?.role === "workspace_admin";

  // Check if opportunity is expired based on submission deadline
  const isExpired = opportunityData?.settings?.submissionDeadline
    ? new Date(opportunityData.settings.submissionDeadline) < new Date()
    : false;

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-[60vh]">
          <Skeleton className="w-full h-full rounded-lg" />
        </div>
      </PageContainer>
    );
  }

  if (!opportunityData) {
    return (
      <PageContainer>
        <div className="text-center py-12 ">
          <h2 className="text-xl font-semibold text-gray-900">
            Opportunity not found
          </h2>
          <p className="text-gray-600 mt-2">
            The opportunity you're looking for doesn't exist.
          </p>
          <Button
            onClick={() => navigate("/dashboard/opportunities")}
            className="mt-4"
          >
            Back to Opportunities
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer scrollable={true}>
      <div className="h-full bg-white p-5">
        <div className="max-w-3xl space-y-6">
          <div>
            <h1 className="text-2xl text-black font-bold text-foreground font-grotesk capitalize">
              {opportunityData?.title}
            </h1>
          </div>
          {/* Hero Image */}
          <div className="w-full aspect-video bg-muted rounded-lg overflow-hidden">
            <img
              src={opportunityData?.media?.url}
              alt="Visa Accelerator Program meeting room"
              width={800}
              height={400}
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* Metadata and Apply Section */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="space-y-3">
              <div className="flex flex-col gap-4 md:flex-row md:gap-8">
                <div>
                  <p className="text-xs text-[#2658AB] font-grotesk font-bold ">
                    Created by
                  </p>
                  <p className="font-medium text-[#101828] font-grotesk">
                    {opportunityData?.ownerEntity?.name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#2658AB] font-grotesk font-bold ">
                    Published on
                  </p>
                  <p className="font-medium text-[#101828] font-grotesk">
                    {new Date(opportunityData?.createdAt).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }
                    )}
                  </p>
                </div>
              </div>
            </div>
            {isOwnOpportunity ? (
              <Button
                onClick={() => navigate(`/dashboard/opportunities/${id}/edit`)}
                size="lg"
                className="bg-[#2658AB] hover:bg-[#2658AB]/90 text-white"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit Opportunity
              </Button>
            ) : !isWorkspaceAdmin ? (
              <Button
                onClick={() => {
                  if (opportunityData?.url) {
                    window.open(opportunityData.url, "_blank", "noopener,noreferrer");
                    return;
                  }
                  navigate(`/dashboard/signify-interest/${id}`);
                }}
                size="lg"
                className="bg-[#2658AB] hover:bg-[#2658AB]/90 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isExpired}
                title={isExpired ? "This opportunity has expired" : ""}
              >
                {isExpired ? "Expired" : "Apply"}
              </Button>
            ) : null}
          </div>

          {/* Introduction Section */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-[#101828] font-grotesk">
              Summary
            </h2>
            <p className="text-[#667085] leading-relaxed font-grotesk">
              {opportunityData?.summary}
            </p>
          </section>
        </div>
      </div>
    </PageContainer>
  );
}
